import { useQuery } from '@tanstack/react-query';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

export interface VendorCall {
  id: number;
  type: 'call' | 'sms_conversation' | 'sms';
  call_sid?: string; // For calls
  message_sid?: string; // For individual SMS
  vendor_name: string;
  phone_number: string;
  search_query: string;
  property_address?: string;
  urgency?: string;
  requirements?: string;
  summarized_transcript?: string;
  status: string;
  
  // Call-specific fields
  is_voicemail?: boolean;
  voicemail_message_left?: boolean;
  call_ended_reason?: string;
  has_recording?: boolean;
  audio_url?: string;
  
  // SMS conversation-specific fields (for grouped conversations)
  search_id?: string;
  vendor_count?: number;
  vendors_contacted?: string[];
  message_count?: number;
  outbound_messages?: number;
  inbound_messages?: number;
  conversation_summary?: Array<{
    direction: string;
    vendor: string;
    content: string;
    timestamp: string;
  }>;
  primary_message_sid?: string;
  latest_message_sid?: string;
  
  // Individual SMS fields (for single SMS messages)
  message_content?: string;
  direction?: 'inbound' | 'outbound';
  
  // Common fields
  has_transcript: boolean;
  has_quote: boolean;
  created_at: string;
  updated_at?: string;
  quote_summary?: {
    available: boolean;
    price_range?: string;
    timeline?: string;
    services_offered?: string[];
    pricing_info?: string[];
    availability?: string;
    requirements?: string;
    follow_up_needed?: boolean;
  };
}

export interface CallHistoryResponse {
  success: boolean;
  data: {
    calls: VendorCall[];
    total_count: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

interface CallHistoryParams {
  searchQuery?: string;
  limit?: number;
  offset?: number;
  communicationType?: 'calls' | 'sms' | 'all';
}

const METHOD = 'GET';
const ENDPOINT = 'vendor/call-history';

export const QUERY_KEY = (params?: CallHistoryParams) => 
  getQueryKey(METHOD, ENDPOINT, params);

export const useGetCallHistory = (params?: CallHistoryParams, enabled = true) => {
  const { session } = useAuth();

  return useQuery({
    enabled: enabled && !!session?.access_token,
    queryKey: QUERY_KEY(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.searchQuery) queryParams.append('search_query', params.searchQuery);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());
      if (params?.communicationType) queryParams.append('communication_type', params.communicationType);
      
      const endpoint = queryParams.toString() 
        ? `${ENDPOINT}?${queryParams}`
        : ENDPOINT;
      
      const response = await handleApiCall<CallHistoryResponse>(
        session,
        METHOD,
        endpoint
      );
      return response.data;
    },
  });
};